import { useMutation } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DCSQueryClient } from "../../../../../queryClient";
import { UserRoles } from "../../../../../constants/userRoles";
import { useToast } from "../../../../../Providers/ToastProvider";
import { useUser } from "../../../../../keycloak/hooks";
import { requests } from "../../../../../services/http-common";
import {
  ContractStatusEnum,
  IContract,
} from "../../../Contracts/Contracts.model";
import { LeadStatusEnum } from "../../Lead.model";
import { toMessageText } from "../../../../../helpers/helperFunctions";
import { sifUrl } from "./sifUrl";
import { ConfirmationModal } from "../../../../../components/ConfirmationModal/ConfirmationModal";

export const Actions = (rowData: {
  status: LeadStatusEnum;
  id: string;
  sendStatus: boolean;
  pdfPath: string;
  contract: IContract | null;
  isDisabled: boolean;
}) => {
  const menu = useRef(null);
  const navigate = useNavigate();
  const { show } = useToast();
  const user: any = useUser();
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  const deleteLead = useMutation({
    mutationFn: () => {
      return requests.delete(`/lead/${rowData.id}`);
    },
    onSuccess: () => {
      show({ detail: "The lead was deleted successfully !" });
      DCSQueryClient.invalidateQueries({ queryKey: ["leads-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const items = useMemo(() => {
    let _items: any = [];

    if (
      rowData.status === LeadStatusEnum.Approved &&
      !rowData.contract &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_CREATE)
    ) {
      _items = [
        {
          label: "Create Contract",
          command: () => {
            navigate(`/sales/contract/create/${rowData.id}`);
          },
        },
        ..._items,
      ];
    }

    if (
      (rowData.status === LeadStatusEnum.Pending ||
        rowData.status === LeadStatusEnum.Approved) &&
      !rowData.sendStatus &&
      user?.roles.dcs_client.includes(UserRoles.LEAD_UPDATE)
    ) {
      _items = [
        {
          label: "Edit",
          command: () => {
            navigate(`/sales/leads/${rowData.id}/details/edit`);
          },
        },
        ..._items,
      ];
    }
    if (
      rowData.status === LeadStatusEnum.Approved &&
      rowData.contract &&
      user?.roles.dcs_client.includes(UserRoles.LEAD_VIEW) &&
      (rowData.contract.status === ContractStatusEnum.Approved ||
        rowData.contract.status === ContractStatusEnum.Sent)
    ) {
      _items = [
        {
          label: "View SIF",
          command: async () => {
            // Open the tab synchronously so the popup blocker allows it.
            const tab = window.open("", "_blank");
            try {
              const res = await requests.get(
                `/contract/${rowData.contract!.id}/sif`
              );
              const href = sifUrl(
                process.env.REACT_APP_DSC_SERVICE_DOMAIN!,
                res.pdfPath
              );
              if (tab) tab.location.href = href;
              else window.open(href, "_blank");
            } catch (err: any) {
              tab?.close();
              show({
                severity: "error",
                detail: toMessageText(
                  err.response?.data?.error?.message,
                  "Could not open the SIF"
                ),
              });
            }
          },
        },
        ..._items,
      ];
    }

    if (user?.roles.dcs_client.includes(UserRoles.LEAD_DELETE)) {
      _items = [
        ..._items,
        {
          label: "Delete",
          command: () => {
            setShowDeletePopup(true);
          },
        },
      ];
    }

    return _items;
  }, [deleteLead, navigate, rowData, user]);

  return (
    <>
      <Menu model={items} popup ref={menu} id="popup_menu" />
      <ConfirmationModal
        visible={showDeletePopup}
        onHide={() => setShowDeletePopup(false)}
        accept={() => deleteLead.mutate()}
        reject={() => setShowDeletePopup(false)}
      />
      <Button
        icon="pi pi-ellipsis-v"
        //@ts-ignore
        onClick={(event) => menu.current.toggle(event)}
        aria-controls="popup_menu"
        aria-haspopup
        className="p-button-rounded p-button-text"
        disabled={!items.length || rowData.isDisabled}
      />
    </>
  );
};

